import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
} from '@mui/material';
import BlockContent from '@sanity/block-content-to-react';
import { useGlobalState } from '../utils/GlobalStateProvider';
import { Product } from '../cms/types';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import { ROOTINE_WEB_URL, SHOPIFY_VARIANT_MAPPINGS } from '../config';
import Image from 'mui-image';
import { colors, theme } from '../utils/theme';
import RootineButton from '../atoms/RootineButton';

export interface CartProps {
  products: string[];
  bypassModal: boolean;
  bgColor?: string;
}

const modalBox = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 3,
  minWidth: { xs: '85%', md: '40%' },
  maxWidth: { xs: '85%', md: '50%' },
  minHeight: { xs: '40%', md: '40%' },
  maxHeight: { xs: '80%', md: '65%' },
  overflow: 'scroll',
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-track': { background: '#F8F8F8', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: 5 },
  '&::-webkit-scrollbar:horizontal': {
    height: 0,
    width: 0,
    display: 'none',
  },
};

export const Cart = (props: CartProps): JSX.Element => {
  const [cart, setCart] = useState<Product[]>([]);
  const [checkoutLink, setCheckoutLink] = useState<string>('');
  const [cartTotal, setCartTotal] = useState<string>('0');
  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const context = useGlobalState();
  const handleModalClose = () => setModalOpen(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let cartProducts: Product[] = [];
    if (context.state.products.size > 0) {
      props.products.forEach((productCode: string) => {
        const productInfo = context.state.products.get(productCode);
        if (productInfo) {
          cartProducts.push(productInfo);
        }
      });
      let bundleProducts: { [name: string]: string[] } = {};
      for (const [productCode, product] of Array.from(context.state.products)) {
        if (product.bundleItems && product.bundleItems.length > 1) {
          let bundleItems = [];
          for (const item of product.bundleItems) {
            bundleItems.push(item.code);
          }
          bundleProducts[productCode] = bundleItems;
        }
      }
      setCart(cartProducts);
      setCheckoutUrl(cartProducts);
    }
  }, [context.state.products]);

  const removeProduct = (productCode: string) => {
    const remainingProducts = cart.filter((item) => item.code !== productCode);
    setCart(remainingProducts);
    setCheckoutUrl(remainingProducts);
  };

  const setCheckoutUrl = (products: Product[]) => {
    const domain = ROOTINE_WEB_URL;
    let total = 0;

    // Check if all three tests are in the products
    const containsAllTests =
      products.some((p) => p.code === 'bm-test') &&
      products.some((p) => p.code === 'bv-test') &&
      products.some((p) => p.code === 'd-test');

    if (containsAllTests) {
      // Replace bm-test, bv-test, and d-test with bnd-bmbvd-test
      products = products.filter(
        (p) =>
          p.code !== 'bm-test' && p.code !== 'bv-test' && p.code !== 'd-test'
      );
      products.push({
        title: 'Complete Nutrition Test Bundle',
        id: '167',
        slug: 'three-test-bundle',
        heroImage: [{ alt: '', id: '', src: '', aspectRatio: 1 }],
        thumbnailImage: { alt: '', id: '', src: '', aspectRatio: 1 },
        code: 'bnd-bmbvd-test',
        price: 577.99, // Use the price from mappings
      });
    }

    // Filter out products with valid IDs
    const filteredProducts = products.filter((item) => {
      const productDetails = SHOPIFY_VARIANT_MAPPINGS[item.code] || {};
      return productDetails.id;
    });

    if (filteredProducts.length === 0) {
      setCheckoutLink(''); // No valid products
      setCartTotal(total.toString());
      return;
    }

    // Create URLs using the URL and URLSearchParams API
    const clearCartURL = new URL(`${domain}/cart/clear`);
    const addCartURL = new URL(`${domain}/cart/add`);

    // Construct the product parameters using URLSearchParams
    filteredProducts.forEach((item, index) => {
      const productDetails = SHOPIFY_VARIANT_MAPPINGS[item.code];
      const { id, selling_plan } = productDetails;
      const quantity = 1;

      if (id) {
        addCartURL.searchParams.set(`items[${index}][id]`, id);
        addCartURL.searchParams.set(
          `items[${index}][quantity]`,
          quantity.toString()
        );

        // Only add selling_plan if product is 'vit-sus'
        if (item.code === 'vit-sus' && selling_plan) {
          addCartURL.searchParams.set(
            `items[${index}][selling_plan]`,
            selling_plan
          );
        }
      }

      // Calculate total price
      total += item.price || 0;
    });

    // Construct the final checkout URL
    const checkoutURL = new URL(`${domain}/checkout`);
    addCartURL.searchParams.set('return_to', checkoutURL.pathname);

    clearCartURL.searchParams.set(
      'return_to',
      `${addCartURL.pathname}${addCartURL.search}`
    );

    // Set the checkout link and total values
    setCheckoutLink(clearCartURL.toString());
    setCartTotal(total.toString());
  };

  const getPrice = (item: Product) => {
    let result = Number.isSafeInteger(item.price)
      ? `$${item.price}`
      : `$${item.price}0`;
    if (item.subscription_term) {
      result += ' ' + item.textPriceLine1;
    }
    return result;
  };

  const renderProduct = (item: Product, idx: number): JSX.Element => {
    const grid1Size = props.bypassModal ? 2 : 2;
    const grid2Size = props.bypassModal ? 8 : 8;
    const grid3Size = props.bypassModal ? 2 : 2;
    return (
      <Grid
        container
        sx={{ padding: { xs: 0, sm: 1 } }}
        key={idx}
        justifyContent="center"
      >
        <Card
          sx={{
            width: { xs: '100%', sm: '90%' },
            display: 'inline-block',
            boxShadow: 'none',
            background: props.bgColor && props.bgColor,
            borderBottom: '2px solid black',
            paddingBottom: 4,
          }}
        >
          <Grid
            container
            sx={{ padding: 2 }}
            key={idx}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item container xs={grid1Size}>
              <Image
                src={item.thumbnailImage.src}
                alt={item.thumbnailImage.alt}
                style={{
                  maxHeight: isMobile ? 75 : 100,
                  border: `1px solid ${colors.greys.SUBDUED_GREY}`,
                  width: '100%',
                  borderRadius: 2,
                }}
              />
            </Grid>
            <Grid item xs={grid2Size}>
              <Typography
                variant="light"
                component="p"
                align="left"
                sx={{
                  fontSize: { xs: 12, md: 16 },
                  position: 'relative',
                  left: 20,
                  overflowWrap: 'break-word',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="light"
                component="div"
                align="left"
                sx={{
                  fontSize: { xs: 10, md: 12 },
                  position: 'relative',
                  top: 20,
                  left: 20,
                  width: '90%',
                  overflowWrap: 'break-word',
                }}
              >
                <BlockContent blocks={item.description} />
              </Typography>
            </Grid>
            <Grid
              item
              xs={grid3Size}
              sx={{ padding: 1, paddingBottom: 0, textAlign: 'right' }}
            >
              <Typography
                variant="light"
                component="p"
                sx={{ fontSize: { xs: 12, sm: 14, lg: 16 } }}
              >
                {getPrice(item)}
              </Typography>
              {cart.length > 1 && (
                <>
                  <CircleIcon
                    sx={{
                      fontSize: { xs: 10, sm: 12 },
                      cursor: 'pointer',
                      position: 'relative',
                      top: 1,
                      display: { xs: 'none', md: 'inline-block' },
                    }}
                  ></CircleIcon>
                  <Typography
                    variant="light"
                    sx={{
                      fontSize: { xs: 10, sm: 12 },
                      pl: { xs: 0, sm: 1 },
                      cursor: 'pointer',
                    }}
                    onClick={() => removeProduct(item.code)}
                  >
                    Remove
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Card>
      </Grid>
    );
  };

  const cartContent = (): JSX.Element => {
    return (
      <Box sx={props.bypassModal ? {} : modalBox}>
        <Grid container>
          {!props.bypassModal && (
            <Grid
              item
              container
              xs={12}
              direction="column"
              alignItems="flex-end"
              justifyContent="flex-end"
            >
              <CloseIcon
                color="disabled"
                onClick={handleModalClose}
                sx={{
                  paddingRight: 3,
                  paddingTop: 1.5,
                  cursor: 'pointer',
                }}
              />
            </Grid>
          )}
          {cart.map((item: Product, idx: number) => {
            return renderProduct(item, idx);
          })}
          <Grid
            item
            xs={0}
            md={props.bypassModal ? 0 : 6}
            sx={{ position: 'sticky', bottom: 0 }}
          ></Grid>
          <Grid
            container
            item
            xs={12}
            md={props.bypassModal ? 12 : 6}
            sx={{ position: 'sticky', bottom: 10, paddingTop: 5 }}
            justifyContent="center"
          >
            {!props.bypassModal && (
              <Grid container xs={12} justifyContent="center" sx={{ mb: 2 }}>
                <CircleIcon sx={{ fontSize: 12, pr: 0.5 }} color="info" />
                <CircleIcon sx={{ fontSize: 12, pl: 0.5 }} color="info" />
              </Grid>
            )}

            <RootineButton
              id="buy-now-btn"
              variation="primary"
              size="medium"
              label={`Buy Now - $${cartTotal}`}
              sx={{
                width: '90%',
              }}
              url={checkoutLink}
              as="a"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {!props.bypassModal && (
        <Modal open={modalOpen} onClose={handleModalClose}>
          {cartContent()}
        </Modal>
      )}
      {props.bypassModal && cartContent()}
    </>
  );
};
